/**
 * The dashboard SPA.
 *
 * A static build with no server runtime (spec §2): the whole of it is files, and every
 * decision is behind the API. Two consequences that are enforced rather than intended —
 * `apps/app` may not import `packages/db` or `packages/barcode` at any distance
 * (dependency-cruiser), and the built bundle may name no Node built-in
 * (`scripts/check-spa-bundle.ts`, which reads the build output rather than the source,
 * because Vite externalises builtins with a warning and the bundle then fails at runtime
 * in exactly the browser nobody tested).
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

import { queryClient } from "./api/client.js";
import { router } from "./router.js";

// The base normalisation first, so every rule after it can be overridden by a component
// that means to. Document-scoped for the same reason `fonts.css` is, and an app-rail file
// rather than a `packages/ui` one because a package consumed as CSS must not reset
// documents it does not own — see its header.
import "./base.css";

// The faces, before anything that uses them. Imported at the entry rather than beside
// `tokens.css` in each component because `@font-face` is document-scoped: declaring it
// once is the whole requirement, and repeating it per component would be five copies of
// one fact. Sign-in renders outside the shell and needs them too.
import "./fonts.css";

const root = document.getElementById("root");
if (!root) throw new Error("#root is missing from index.html");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
